import { memo, useCallback, useMemo } from "react";
import { Button, Col, Form, OverlayTrigger, Row } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useSaveUser } from "../../../api/mutations/useSaveUser";
import { useGetUser } from "../../../api/queries/useGetUser";
import { useAppContext } from "../../../Context/AppContext";
import { personalInfoSchema } from "../../schemas/Schema";
import SelectInput from "../../common/SelectInput";
import { BusinessMembershipFormData } from "../../../types/UserData";
import { ImageUrls } from "../../../types/Types";
import { SectionHeader } from "../BusinessMembershipForm";
import { countryOptions, renderTooltip } from "../YouthMembershipForm";
import { useTranslation } from "react-i18next";
import { SORTED_SECTORS } from "../../../utils/sectors";

const FORM_MIN_HEIGHT = 640;
const FORM_ROW_STYLE = { minHeight: FORM_MIN_HEIGHT };
const PHOTO_TOOLTIP_MSG =
  "PNG, JPG, JPEG, HEIF, HEIC, PDF, Min 200x200px, Max 5MB";

const GENDERS = ["Male", "Female"] as const;

const LANGUAGE_OPTIONS = [
  { value: "English", label: "English" },
  { value: "Arabic", label: "Arabic" },
  { value: "Turkish", label: "Turkish" },
  { value: "Bahasa Indonesia", label: "Bahasa Indonesia" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "Malay", label: "Malay" },
  { value: "Albanian", label: "Albanian" },
  { value: "Bosnian", label: "Bosnian" },
  { value: "Urdu", label: "Urdu" },
] as const;

interface FormProps {
  defaultData?: BusinessMembershipFormData;
  showEditModal?: boolean;
  setShowEditModal?: (show: boolean) => void;
  defaultImages?: ImageUrls;
  isAdmin?: boolean;
  handleNext: () => void;
}

interface FormType {
  personalGender: string;
  personalLanguagePreference?: string;
  personalAbout?: string | null;
  personalPositionCompany: string;
  businessName: string;
  businessLogo?: any;
  businessSectorIndustry: string;
  businessWebsiteUrl?: string;
  businessCountryOperation: string;
  businessAbout?: string | null;
}

function PersonalInfoFormInner({
  showEditModal,
  defaultData,
  isAdmin,
  defaultImages,
  handleNext,
}: FormProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    control,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(personalInfoSchema),
    defaultValues: {
      personalGender: defaultData?.personalGender ?? "",
      personalLanguagePreference: defaultData?.personalLanguagePreference ?? "",
      personalAbout: defaultData?.personalAbout ?? "",
      personalPositionCompany: defaultData?.personalPositionCompany ?? "",
      businessName: defaultData?.businessName ?? "",
      businessLogo: defaultImages?.BusinessLogo ?? "",
      businessSectorIndustry: defaultData?.businessSectorIndustry ?? "",
      businessWebsiteUrl: defaultData?.businessWebsiteUrl ?? "",
      businessCountryOperation: defaultData?.businessCountryOperation ?? "",
      businessAbout: defaultData?.businessAbout ?? "",
    },
  });
  const languageOptionsList = useMemo(() => [...LANGUAGE_OPTIONS], []);
  const photoTooltipOverlay = useMemo(
    () => renderTooltip(PHOTO_TOOLTIP_MSG),
    []
  );
  const { objectId } = useAppContext();
  const { data: userData } = useGetUser({ id: objectId || "" });
  const queryClient = useQueryClient();

  const { mutate: updateUser, isPending } = useSaveUser({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      toast.success(t("feedback.userUpdatedSuccess"));
      handleNext();
    },
    onError: () => {
      toast.error(t("feedback.userUpdateFailed"));
    },
  });

  const onSubmit = useCallback(
    (data: FormType) => {
      if (errors.businessLogo) {
        setError("businessLogo", {
          type: "manual",
          message: errors.businessLogo.message,
        });
        return;
      }
      if (!isDirty) {
        handleNext();
        return;
      }
      const formData = new FormData();
      const mergedData = {
        ...userData?.user,
        ...data,
      };

      Object.entries(mergedData).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          key !== "businessLogo" &&
          key !== "professionalPhoto" &&
          key !== "personalProfessionalPhoto"
        ) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const logo = (document.getElementById("businessLogo") as HTMLInputElement)
        ?.files?.[0];
      if (logo) {
        formData.append("businessLogo", logo);
      }
      localStorage.setItem("UserProfile", JSON.stringify(mergedData));
      updateUser(formData);
    },
    [
      errors.businessLogo,
      isDirty,
      userData?.user,
      handleNext,
      updateUser,
      setError,
    ]
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row
        className="mb-4 shadow-sm rounded-4 py-3"
        style={FORM_ROW_STYLE}
      >
        <Col md={12}>
          <SectionHeader
            title="User Personal Information"
            tooltip='Sharing your gender and language preferences allows us to personalise your experience and communication on the platform. Your "About You" section makes your profile engaging and approachable, while your position clarifies your role, helping others understand who they’re connecting with.'
          />
        </Col>
        <Col md={6}>
          <Form.Group controlId="personalGender">
            <Form.Label>Gender*</Form.Label>
            <Form.Select
              {...register("personalGender")}
              isInvalid={!!errors.personalGender}
              defaultValue={defaultData?.personalGender || ""}
            >
              <option value="" disabled hidden>
                Select Gender...
              </option>
              {GENDERS.map((gender) => (
                <option key={gender} value={gender}>
                  {gender}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback
              className="text-danger px-1"
              type="invalid"
            >
              {errors.personalGender?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="personalLanguagePreference">
            <SelectInput
              name="personalLanguagePreference"
              label="Language Preference"
              placeholder="Select Language"
              control={control}
              options={languageOptionsList}
              error={errors.personalLanguagePreference}
              isRequired
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="personalAbout" className="mt-3">
            <Form.Label>About You</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("personalAbout")}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="personalPositionCompany" className="mt-3">
            <Form.Label>Position in Company*</Form.Label>
            <Form.Control
              type="text"
              {...register("personalPositionCompany")}
              isInvalid={!!errors.personalPositionCompany}
            />
            <Form.Control.Feedback
              className="text-danger px-1"
              type="invalid"
            >
              {errors.personalPositionCompany?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col className="mt-3" md={12}>
          <SectionHeader
            title="Business Identification Information"
            tooltip='We ask for your business name, logo, and industry to showcase your brand and help others recognise your business on our platform. The "About the Business" section and website let you share your story and expertise, while listing countries of operation helps connect you with the right partners or clients in your regions of focus.'
          />
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessName">
            <Form.Label>Business Name*</Form.Label>
            <Form.Control
              disabled={showEditModal && !isAdmin}
              type="text"
              {...register("businessName")}
              isInvalid={!!errors.businessName}
            />
            <Form.Control.Feedback
              className="text-danger px-1"
              type="invalid"
            >
              {errors.businessName?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessLogo">
            <OverlayTrigger placement="right" overlay={photoTooltipOverlay}>
              <Form.Label>Logo</Form.Label>
            </OverlayTrigger>
            <Form.Control
              type="file"
              accept=".png,.jpg,.jpeg,.heif,.heic,image/png,image/jpeg,image/heif,image/heic"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  const maxSize = 5 * 1024 * 1024;
                  const validTypes = [
                    "image/png",
                    "image/jpeg",
                    "image/heif",
                    "image/heic",
                  ];

                  if (!validTypes.includes(file.type)) {
                    setError("businessLogo", {
                      type: "manual",
                      message:
                        "Unsupported File Format. Please upload an image (PNG, JPG, HEIF, or HEIC).",
                    });
                    (e.target as HTMLInputElement).value = "";
                    return;
                  }

                  if (file.size > maxSize) {
                    setError("businessLogo", {
                      type: "manual",
                      message: "File size must be less than 5MB",
                    });
                    (e.target as HTMLInputElement).value = "";
                    return;
                  }

                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const base64String = reader.result?.toString();
                    if (!base64String) {
                      setError("businessLogo", {
                        type: "manual",
                        message: "File upload failed. Please try again.",
                      });
                      return;
                    }

                    const img = new Image();
                    img.onload = () => {
                      if (img.width < 200 || img.height < 200) {
                        setError("businessLogo", {
                          type: "manual",
                          message: "Image must be at least 200x200 pixels",
                        });
                        return;
                      }

                      clearErrors("businessLogo");
                      const event = {
                        target: {
                          name: "businessLogo",
                          value: base64String,
                        },
                      };
                      register("businessLogo").onChange(event);
                    };
                    img.onerror = () => {
                      setError("businessLogo", {
                        type: "manual",
                        message: "File upload failed. Please try again.",
                      });
                    };
                    img.src = base64String;
                  };

                  reader.readAsDataURL(file);
                }
              }}
              isInvalid={!!errors.businessLogo}
            />
            <Form.Control.Feedback
              className="text-danger px-1"
              type="invalid"
            >
              {errors.businessLogo?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group controlId="sector" className="mt-3">
            <Form.Label>Sector / Industry*</Form.Label>
            <Form.Select
              {...register("businessSectorIndustry")}
              isInvalid={!!errors.businessSectorIndustry}
              defaultValue={defaultData?.businessSectorIndustry || ""}
            >
              <option value="" disabled hidden>
                Select Sector/Industry...
              </option>
              {SORTED_SECTORS.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback
              className="text-danger px-1"
              type="invalid"
            >
              {errors.businessSectorIndustry?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessWebsiteUrl" className="mt-3">
            <Form.Label>Website URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://www.example.com"
              {...register("businessWebsiteUrl", {
                onBlur: (e) => {
                  let v = e.target.value.trim();
                  if (!v) return;
                  if (!/^https?:\/\//i.test(v)) v = "https://" + v;
                  try {
                    const u = new URL(v);
                    if (!u.hostname.startsWith("www."))
                      u.hostname = "www." + u.hostname;
                    setValue("businessWebsiteUrl", u.toString(), {
                      shouldValidate: true,
                    });
                  } catch {
                    setValue("businessWebsiteUrl", v, { shouldValidate: true });
                  }
                },
              })}
              isInvalid={!!errors.businessWebsiteUrl}
            />
            <Form.Control.Feedback
              className="text-danger px-1"
              type="invalid"
            >
              {errors.businessWebsiteUrl?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessCountryOperation" className="mt-3">
            <SelectInput
              disabled={showEditModal && !isAdmin}
              name="businessCountryOperation"
              label="Countries of Operation"
              placeholder="Select all Countries that apply"
              control={control}
              options={countryOptions}
              error={errors.businessCountryOperation}
              isRequired
              isMulti
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="businessAbout" className="mt-3">
            <Form.Label>About the Business</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("businessAbout")}
            />
          </Form.Group>
        </Col>
        <Col className="mt-3">
          <div className="d-flex justify-content-end">
            <Button disabled={isPending} variant="primary" type="submit">
              {isPending ? "Saving..." : "Save & Continue"}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
}

PersonalInfoFormInner.displayName = "PersonalInfoForm";

const PersonalInfoForm = memo(PersonalInfoFormInner);
PersonalInfoForm.displayName = "PersonalInfoForm";

export default PersonalInfoForm;
